import { Fragment, useCallback, useEffect, useState } from 'react';
import { Collapse } from '@mui/material';

import { Coach } from 'src/models/Coach';
import { Equipment } from 'src/models/Equipment';
import { ExtraFile } from 'src/models/ExtraFile';
import { Style } from 'src/models/Style';
import { Tag } from 'src/models/Tag';
import { Type } from 'src/models/Type';
import { Video } from 'src/models/video';
import { Session as SessionInterface } from 'src/models/Session';
import { FilterTrait } from 'src/models/SessionFilters';
import { Challenge, ChallengeSession } from 'src/models/Challenge';
import {
  EquipmentBadge,
  VimeoPlayer,
  DurationIndicator,
  DifficultyIndicators,
  Avatar,
} from 'src/components';
import { isNativePlatform } from '../../config';

import Downloads from './Downloads';
import { composeFilterLink } from './helpers';

import {
  Container,
  Separator,
  Title,
  Underline,
  SectionTitle,
  PrimaryDescription,
  SecondaryDescription,
  EquipmentSection,
  EquipmentList,
  EquipmentItem,
  PropertiesAndActions,
  Properties,
  Property,
  PropertyLabel,
  PropertyLink,
  TypeList,
  TypeItem,
  Actions,
  Tags,
  TagList,
  TagToggler,
  TagItem,
  CoachSection,
  CoachList,
  CoachCard,
  CoachSeparator,
  DownloadsSection,
} from './styles';
import MobileCollapsible from './MobileCollapsible';
import {
  CalendarButton,
  LikeButton,
  CompletedButton,
  PublicLikeButton,
} from './ActionButtons';

export type ContentType =
  SessionInterface["contentType"] |
  Challenge["contentType"] |
  ChallengeSession["contentType"];

/**
 * Used for displaying details of all session-like entities:
 * - sessions
 * - challenges
 * - challenge sessions
 */
const Session: React.FC<{
  contentType: ContentType;
  title: string;
  underline: string;
  difficulty: number;
  primaryDescription: string;
  secondaryDescription: string;
  coach: Coach[];
  style?: Style | null;
  type?: Type[];
  tag: Tag[];
  equipment: Equipment[];
  video: Video | null;
  attachments: Array<ExtraFile>;
  horizontalThumbnail?: string | null;
  duration?: number | null;
  isLiked?: boolean;
  likesCount?: number;
  isLikedByUser?: boolean;
  isCompleted?: boolean;
  toggleLike?: (status: boolean) => void;
  togglePublicLike?: () => Promise<{ liked: boolean; likes_count: number }>;
  toggleCompleted?: (status: boolean) => void;
  playlistActions?: React.ReactNode;
  onVideoPlaybackComplete?: () => void;
  entityId?: string;
  entityType?: 'session' | 'challenge-session';
}> = ({
  contentType,
  title,
  underline,
  difficulty,
  primaryDescription,
  secondaryDescription,
  coach,
  style,
  type,
  tag,
  equipment,
  video,
  attachments,
  horizontalThumbnail,
  duration,
  isLiked: initialIsLiked,
  likesCount: initialLikesCount,
  isLikedByUser: initialIsLikedByUser,
  isCompleted,
  toggleLike,
  togglePublicLike,
  toggleCompleted,
  playlistActions,
  onVideoPlaybackComplete,
  entityId,
  entityType,
}) => {
  const [tagsOpened, setTagsOpened] = useState(false);

  const [isLiked, setIsLiked] = useState(initialIsLiked);
  useEffect(() => setIsLiked(initialIsLiked), [initialIsLiked]);
  const handleToggleLike = useCallback(() => {
    if (!toggleLike) return;
    toggleLike(!isLiked);
    setIsLiked(!isLiked);
  }, [toggleLike, isLiked]);
  const [likesCount, setLikesCount] = useState(initialLikesCount ?? 0);
  const [isLikedByUser, setIsLikedByUser] = useState(
    initialIsLikedByUser ?? false,
  );
  const [isPublicLikePending, setIsPublicLikePending] = useState(false);

  useEffect(() => {
    setLikesCount(initialLikesCount ?? 0);
  }, [initialLikesCount]);

  useEffect(() => {
    setIsLikedByUser(initialIsLikedByUser ?? false);
  }, [initialIsLikedByUser]);

  const handleTogglePublicLike = useCallback(async () => {
    if (!togglePublicLike || isPublicLikePending) return;

    const previousLiked = isLikedByUser;
    const previousCount = likesCount;
    const optimisticLiked = !previousLiked;
    const optimisticCount = Math.max(
      0,
      previousCount + (optimisticLiked ? 1 : -1),
    );

    setIsLikedByUser(optimisticLiked);
    setLikesCount(optimisticCount);
    setIsPublicLikePending(true);

    try {
      const response = await togglePublicLike();
      setIsLikedByUser(response?.liked ?? optimisticLiked);
      setLikesCount(response?.likes_count ?? optimisticCount);
    } catch (_error) {
      setIsLikedByUser(previousLiked);
      setLikesCount(previousCount);
    } finally {
      setIsPublicLikePending(false);
    }
  }, [isLikedByUser, isPublicLikePending, likesCount, togglePublicLike]);

  const validAttachments = attachments.filter(extra => extra.url);

  return (
    <Container>
      <Title>{title}</Title>
      <Underline>{underline}</Underline>
      {
        !isNativePlatform &&
          <MobileCollapsible>
              <PrimaryDescription>
                  <div dangerouslySetInnerHTML={{__html: primaryDescription}} />
              </PrimaryDescription>
          </MobileCollapsible>
      }
      {equipment.length > 0 && (
        <>
          <Separator />
          <EquipmentSection>
            <SectionTitle>Przygotuj:</SectionTitle>
            <EquipmentList>
              {equipment.map(item =>(
                <EquipmentItem key={item.id}>
                  <EquipmentBadge text={item.name} />
                </EquipmentItem>
              ))}
            </EquipmentList>
          </EquipmentSection>
        </>
      )}
      {(video || horizontalThumbnail) && (
        <VimeoPlayer
          viewLink={video?.viewLink}
          horizontalThumbnail={horizontalThumbnail}
          isCompleted={isCompleted}
          onComplete={() => toggleCompleted?.(true)}
          onEnd={onVideoPlaybackComplete}
          entityId={entityId}
          entityType={entityType}
        />
      )}
      <PropertiesAndActions>
        <Properties>
          {!!duration && (
            <Property>
              <PropertyLabel>Czas trwania</PropertyLabel>
              <DurationIndicator duration={duration ?? 0} size="big"/>
            </Property>
          )}
          <Property>
            <PropertyLabel>Poziom</PropertyLabel>
            <DifficultyIndicators
              difficulty={difficulty || 0}
              maxDifficulty={5}
            />
          </Property>
          {style && (
            <Property>
              <PropertyLabel>Styl Jogi</PropertyLabel>
              <PropertyLink to={composeFilterLink(contentType, FilterTrait.Style, style.id)}>
                {style.name}
              </PropertyLink>
            </Property>
          )}
          {(type?.length ?? 0) > 0 && (
            <Property>
              <PropertyLabel>Rodzaj Jogi</PropertyLabel>
              <TypeList>
                {type?.map(t => (
                  <TypeItem key={t.id}>
                    <PropertyLink to={composeFilterLink(contentType, FilterTrait.Type, t.id)}>
                      {t.name}
                    </PropertyLink>
                  </TypeItem>
                ))}
              </TypeList>
            </Property>
          )}
        </Properties>
        <Actions>
          {isCompleted !== undefined && (
            <CompletedButton
              completed={isCompleted}
              onToggle={() => toggleCompleted?.(!isCompleted)}
            />
          )}
          {isLiked !== undefined && (
            <LikeButton
              liked={isLiked}
              onToggle={handleToggleLike}
            />
          )}
          {contentType === 'session' && (
            <PublicLikeButton
              liked={isLikedByUser}
              likesCount={likesCount}
              pending={isPublicLikePending}
              onToggle={handleTogglePublicLike}
            />
          )}
          <CalendarButton title={title} />
          {playlistActions &&
            (contentType !== 'challengeSession' || !!video?.viewLink) &&
            playlistActions}
        </Actions>
      </PropertiesAndActions>
      {
        !isNativePlatform &&
          <Tags>
              <Collapse in={tagsOpened}>
                  <TagList>
                    {tag.map(t => (
                      <TagItem
                        key={t.id}
                        to={composeFilterLink(contentType, FilterTrait.Tag, t.id)}
                      >
                        {t.name}
                      </TagItem>
                    ))}
                  </TagList>
              </Collapse>
              <TagToggler onClick={() => setTagsOpened(current => !current)}>
                  <span>{tagsOpened ? 'Schowaj tagi' : 'Pokaż tagi'}</span>
              </TagToggler>
          </Tags>
      }
      {validAttachments?.length > 0 && (
        <DownloadsSection>
          <SectionTitle>Do pobrania:</SectionTitle>
          <Downloads files={validAttachments}/>
        </DownloadsSection>
      )}
      {
        !isNativePlatform &&
          <MobileCollapsible>
              <SecondaryDescription>
                  <div dangerouslySetInnerHTML={{__html: secondaryDescription}} />
              </SecondaryDescription>
          </MobileCollapsible>
      }
      <Separator />
      <CoachSection>
        <SectionTitle>{coach.length > 1 ? 'Nauczyciele:' : 'Nauczyciel:'}</SectionTitle>
        <CoachList>
          {coach.map((c, index) => (
            <Fragment key={c.id}>
              {index > 0 && <CoachSeparator>|</CoachSeparator>}
              <CoachCard
                to={composeFilterLink(contentType, FilterTrait.Coach, c.id)}
              >
                <Avatar size="large" imageSrc={c.thumbnail ?? ''}/>
                <p>{c.name}</p>
              </CoachCard>
            </Fragment>
          ))}
        </CoachList>
      </CoachSection>
    </Container>
  );
};

export default Session;
